/**
 * Static codex registry (Phase 2.3 of the planet gameplay expansion).
 *
 * Codex entries are short, lore-flavoured snippets the player unlocks
 * by investigating ruins, surviving weather events, completing
 * milestone missions, or finishing the Liberate arc. Persisted as a
 * flat `Set<string>` of ids on PlayerData.
 *
 * The registry is intentionally a hand-authored map, not procedural —
 * the goal is a small, curated lore drip that gives ruins / events a
 * narrative payoff. Procedural per-ruin `loreFragmentId`s map onto
 * this same registry via a parametric prefix (`lore_crash_*` etc.):
 * the actual text shown is the first matching base entry.
 *
 * Adding entries: just append. The codex UI tab walks the unlocked
 * set, looks each up via {@link getCodexEntry}, and renders. Missing
 * lookups gracefully fall back to a placeholder title so a new ruin
 * fragment id doesn't crash the dock UI.
 */
const ENTRIES = [
    // ── Crash sites ─────────────────────────────────────────────────
    {
        id: 'lore_crash',
        title: 'Wrecked Hull, Unknown Maker',
        body: 'The crash crater is recent — paint hasn\'t flaked yet. No surviving ' +
            'log buoy, no IFF transponder. Whatever flew this never wanted to be ' +
            'recognised on impact.',
        category: 'event',
    },
    {
        id: 'lore_crash_0',
        title: 'First Wreck',
        body: 'A standard freighter shell, peeled open from the inside out. The ' +
            'galley still has a half-cooked ration tray bolted to the wall.',
        category: 'event',
    },
    {
        id: 'lore_crash_1',
        title: 'Pilot\'s Footprint',
        body: 'Boot tracks lead away from the wreck and stop after twelve metres. ' +
            'No body, no second set of prints to meet them. The wind has not yet ' +
            'erased the trail — someone walked away. Recently.',
        category: 'event',
    },
    {
        id: 'lore_crash_2',
        title: 'Burned Manifest',
        body: 'A fire-blackened crate stamped with a freight-cooperative seal. The ' +
            'manifest beneath the soot mentions a destination that doesn\'t ' +
            'appear in any registered star chart.',
        category: 'event',
    },
    {
        id: 'lore_crash_3',
        title: 'Carbonised Logs',
        body: 'The data recorder survived but the chassis is melted shut. The hull ' +
            'plating on the port flank is scored by something a freighter\'s own ' +
            'thrusters could not have done.',
        category: 'event',
    },
    // ── Precursor ruins ─────────────────────────────────────────────
    {
        id: 'lore_precursor',
        title: 'Glassed Obelisk',
        body: 'A monolith of fused silicate, edged in a metal that won\'t oxidise. ' +
            'The carvings glow faintly when you approach and dim again the moment ' +
            'you back away. They are reacting to you, not to the sun.',
        category: 'precursor',
    },
    {
        id: 'lore_precursor_0',
        title: 'Lattice Glyph',
        body: 'The base of the obelisk is wrapped in a glyph that repeats itself in ' +
            'three rotations. No translation matrix decodes more than the first ' +
            'two characters. They both read like the same word: BEFORE.',
        category: 'precursor',
    },
    {
        id: 'lore_precursor_1',
        title: 'Resonance Hum',
        body: 'When ship power cuts within twenty metres of the obelisk, the ' +
            'reactor restarts on a different tune. Nothing measurable, just ' +
            '… settled. The hum stays in your headset for an hour afterward.',
        category: 'precursor',
    },
    {
        id: 'lore_precursor_2',
        title: 'Counting the Spires',
        body: 'Three precursor obelisks recovered to date share the same prime ' +
            'series stamped along the spine. Someone wanted future visitors to ' +
            'be able to count to them.',
        category: 'precursor',
    },
    {
        id: 'lore_precursor_3',
        title: 'The Quiet War',
        body: 'Carbon dating on the obelisks lands them at roughly forty thousand ' +
            'standard years. The same era as the unexplained collapse of three ' +
            'core sectors. The records say "void incursion." The obelisks say ' +
            'something else.',
        category: 'precursor',
    },
    // ── Colony ruins ────────────────────────────────────────────────
    {
        id: 'lore_colony',
        title: 'Collapsed Prefab',
        body: 'The colony arch and a single dome wall still stand. Inside, a row ' +
            'of dormant maker-units lined up like petrified workers. Power has ' +
            'been gone long enough for the indicator LEDs to leach to grey.',
        category: 'event',
    },
    {
        id: 'lore_colony_0',
        title: 'Roll Call',
        body: 'A scrap of laminated paper, brittle but legible: the colony\'s ' +
            'roster from the last day of operation. Forty-two names. Every one ' +
            'has been crossed off.',
        category: 'event',
    },
    {
        id: 'lore_colony_1',
        title: 'Evacuation Notice',
        body: 'A bulletin board pinned to the chow hall wall reads "ATMOS LOSS ' +
            'NORTHWEST QUAD — EVAC TO SECTION C BY 1900 LOCAL." Below it, in ' +
            'shaky handwriting: "no shuttle is coming."',
        category: 'event',
    },
    {
        id: 'lore_colony_2',
        title: 'Gardener\'s Logbook',
        body: 'A waterproof field notebook from the hydroponics block. The last ' +
            'page is a single sentence repeated for half a page: "they were ' +
            'singing the wrong song." No further context.',
        category: 'event',
    },
    {
        id: 'lore_colony_3',
        title: 'Children\'s Drawing',
        body: 'A wall in the prefab nursery is covered in crayon stick-figures. In ' +
            'each one the sky is dark and there is exactly one star. The same ' +
            'star, every time.',
        category: 'event',
    },
    // ── Liberation cosmetic ─────────────────────────────────────────
    {
        id: 'liberator_badge',
        title: 'Liberator',
        body: 'You broke the pirate stranglehold on a contested world. The locals ' +
            'will remember. The pirates will, too — and so will the next ' +
            'survey crew that lands here in peace.',
        category: 'liberation',
    },
];
const BY_ID = new Map();
for (const e of ENTRIES)
    BY_ID.set(e.id, e);
/**
 * Resolve a fragment id to its codex entry. Falls back to the entry's
 * base kind (e.g. `lore_crash_3 → lore_crash`) so the procedural ruin
 * id maps onto the curated text without authoring a unique entry per
 * generated site. Returns `undefined` only if neither the exact id
 * nor any base prefix is registered.
 */
export function getCodexEntry(id) {
    const direct = BY_ID.get(id);
    if (direct)
        return direct;
    const base = id.replace(/_\d+$/, '');
    return BY_ID.get(base);
}
/**
 * Flat read of every authored entry. Order = insertion order. The
 * client uses this to render a "discovered N of M" header in the
 * codex tab.
 */
export function listCodexEntries() {
    return ENTRIES.slice();
}
//# sourceMappingURL=codex.js.map